import React from "react";
import {
  shorthands,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { getHighContrastNoAdjustStyle } from "@fluentui/style-utilities";
import type { GriffelStyle } from "@fluentui/react-components";

import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";
import {
  menuTextChevronClass,
  menuTextContainerClass,
  menuTextLine2Class,
} from "@1js/acui-button";
import { ActionControlSize, hiddenContentClassName } from "@1js/acui-common";
// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastBackgroundHoverFocus,
  commonHighContrastBorder,
  commonHighContrastButtonRestStyles,
  commonHighContrastColorButtonRest,
  commonHighContrastColorDisabled,
  commonHighContrastColorHoverFocus,
  commonHighContrastDisabledStyles,
  commonHighContrastHoveredDisabledStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastHoverBorderStyles,
  useCommonFocusStyles,
  hiddenContentStyle,
  HighContrastSelector,
  useCommonDisabledStyles,
  minimumTargetSizedStyles,
} from "@1js/acui-common/lib/styling/next/commonStyles.styles";

import { floatieStylingConstants } from "../../../ContextualUI/Controls/floatieStylingConstants";
import { getRibbonBottomBarStylingConstants } from "../../ribbonStylingConstants";
import type { RibbonButtonState } from "../RibbonButton/next/RibbonButton.types";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { AppContext } from "@1js/acui-context";

const borderRadius = 0;
const borderThicknessStandard: string = "1";

export const useRibbonButtonLabelStyles = makeStyles({
  base: {
    paddingLeft: "10px",
    paddingRight: "2px",
    whiteSpace: "nowrap",
  },
  small: {
    paddingLeft: customTokens.spacingHorizontalXS,
    paddingRight: customTokens.spacingHorizontalNone,
  },
  large: {
    paddingLeft: customTokens.spacingHorizontalNone,
    paddingRight: customTokens.spacingHorizontalNone,
  },
});

export const useRibbonButtonLabelNextStyles = makeStyles({
  base: {
    whiteSpace: "nowrap",
    paddingLeft: customTokens.spacingHorizontalXS,
  },
  large: {
    fontSize: customTokens.fontSizeBase200,
    lineHeight: "normal",
    ...shorthands.padding(customTokens.spacingHorizontalNone),
  },
});
export const useCommonRibbonButtonLabelClassNames = (
  size?: RibbonButtonState["size"],
  classNames?: string
) => {
  const nextStylesEnabled = isNextStyleEnabled();
  const ribbonButtonLabelStyles = useRibbonButtonLabelStyles();
  const ribbonButtonLabelNextStyles = useRibbonButtonLabelNextStyles();
  return mergeClasses(
    nextStylesEnabled
      ? ribbonButtonLabelNextStyles.base
      : ribbonButtonLabelStyles.base,
    size === "small" && ribbonButtonLabelStyles.small,
    size === "large" &&
      (nextStylesEnabled
        ? ribbonButtonLabelNextStyles.large
        : ribbonButtonLabelStyles.large),
    classNames
  );
};

/**
 * Holds the common ribbon button styles that gets applied to all types of ribbon buttons
 */
const useCommonRibbonButtonRootStyles = makeStyles({
  base: {
    position: "relative",
    boxShadow: "none",
    backgroundColor: "transparent",
    ...shorthands.borderColor("transparent"),
    ...shorthands.borderRadius(`${borderRadius}px`),
    focusInnerWidth: "0px",
    ...shorthands.borderWidth(`${borderThicknessStandard}px`),
    ...shorthands.borderStyle("solid"),
    ...shorthands.overflow("visible"),
    minWidth: "40px",
    paddingLeft: "9px",
    paddingRight: "9px",
    paddingTop: "0px",
    paddingBottom: "0px",
    ...shorthands.gap("0px"),
    height: "40px",
    minHeight: "40px",
    fontWeight: 400,
    ":hover": {
      backgroundColor: customTokens.legacy_neutralLighter,
      ...shorthands.borderColor("transparent"),
      boxShadow: "none",
      cursor: "default",
    },

    ":active": {
      backgroundColor: customTokens.legacy_neutralQuaternaryAlt,
      ...shorthands.borderColor("transparent"),
      boxShadow: "none",
      color: customTokens.legacy_neutralDark,
      transform: "none",
    },
    ":hover:active": {
      backgroundColor: customTokens.legacy_neutralQuaternaryAlt,
      ...shorthands.borderColor("transparent"),
    },

    [`${HighContrastSelector}`]: {
      ...(getHighContrastNoAdjustStyle() as GriffelStyle),
      ...commonHighContrastButtonRestStyles,
      ...commonHighContrastColorButtonRest,
      ...commonHighContrastBorder,

      ":hover": {
        ...commonHighContrastBackgroundHoverFocus,
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },

      ":hover:active": {
        ...commonHighContrastBackgroundHoverFocus,
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },

      ":focus": {
        ...commonHighContrastBackgroundHoverFocus,
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },
    },

    lineHeight: "1",
    textOverflow: "unset",
    whiteSpace: "normal",
    [`& .${menuTextContainerClass}`]: {
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      ...shorthands.padding("1px"),
      whiteSpace: "nowrap",
      [`${HighContrastSelector}`]: {
        ...(getHighContrastNoAdjustStyle() as GriffelStyle),
      },
    },
    [`& .${menuTextLine2Class}`]: {
      lineHeight: "normal",
    },
    [`& .${menuTextChevronClass}`]: {
      width: "8px",
      height: "8px",
      paddingLeft: "2px",
      paddingRight: "2px",
      paddingTop: "0px",
      paddingBottom: "0px",
      fill: customTokens.legacy_neutralDark,
    },
    // Screen reader text
    [`& .${hiddenContentClassName}`]: {
      ...hiddenContentStyle,
    },
  },
  pixelFocusBorder: {
    ...shorthands.borderRadius(`${borderRadius}px`),
    focusInnerWidth: "0px",
  },
  disabled: {
    backgroundColor: "transparent",
    color: customTokens.legacy_neutralTertiaryAlt,
    ...shorthands.borderColor("transparent"),
    pointerEvents: "none",

    [`${HighContrastSelector}`]: {
      ...commonHighContrastColorDisabled,
      ...commonHighContrastDisabledStyles,
      ":hover": {
        ...commonHighContrastHoveredDisabledStyles,
      },
      ":focus": {
        ...commonHighContrastOutlineFocusedDisabled,
      },
    },
  },
  small: {
    fontSize: "12px",
    fontWeight: 400,
    ...shorthands.gap("0px"),
    height: "24px",
    minHeight: "24px",
    minWidth: "24px",
    ...shorthands.padding("1px"),
    justifyContent: "flex-start",
  },
  large: {
    flexDirection: "column",
    fontSize: "12px",
    fontWeight: 400,
    ...shorthands.gap("0px"),
    height: "72px",
    justifyContent: "flex-start",
    lineHeight: "normal",
  },
  // We need to offset the groupLauncher a bit to move it up to the edge of the divider.
  groupLauncher: {
    ...shorthands.gap("0px"),
    paddingLeft: "2px",
    paddingRight: "2px",
    width: "12px",
    minWidth: "12px",
    minHeight: "12px",
    height: "12px",
    marginRight: `${-getRibbonBottomBarStylingConstants("Multiline")
      .groupDividerPadding.left}px`,
    // the below styles are for the focus border
    justifyContent: "center",
    ":hover": {
      ...shorthands.borderRadius(`${borderRadius}px`),
    },
    ":active": {
      ...shorthands.borderRadius(`${borderRadius}px`),
    },
  },
  groupLauncherWithMinTargetSizes: {
    marginBottom: "8px",
  },
  ribbonModeButton: {
    ...shorthands.gap("0px"),
    height: "16px",
    minHeight: "16px",
    width: "20px",
    minWidth: "20px",
    justifyContent: "flex-start",
    paddingLeft: "3px",
  },
  floatieButton: {
    ...shorthands.gap("0px"),
    fontSize: "12px",
    height: `${floatieStylingConstants.floatieRowHeight}px`,
    minHeight: `${floatieStylingConstants.floatieRowHeight}px`,
    minWidth: `${floatieStylingConstants.floatieRowHeight}px`,
    paddingLeft: "3px",
    paddingRight: "3px",
  },
  floatieLargeButton: {
    flexDirection: "column",
    fontSize: "12px",
    ...shorthands.gap("0px"),
    height: `${floatieStylingConstants.floatieLargeControlHeight}px`,
    justifyContent: "flex-start",
    lineHeight: "normal",
    minHeight: `${floatieStylingConstants.floatieLargeControlHeight}px`,
    minWidth: `${floatieStylingConstants.minimumFloatieLargeControlWidth}px`,
    paddingLeft: "2px",
    paddingRight: "2px",
    paddingTop: "4px",
    paddingBottom: "0px",
    ...shorthands.borderWidth(`${borderThicknessStandard}px`),
  },
  minTargetSizes: {
    ...minimumTargetSizedStyles,
  },
});

const useCommonRibbonButtonRootStylesNext = makeStyles({
  base: {
    ...shorthands.gap("0px"),
    ...shorthands.margin(customTokens.spacingHorizontalNone),
    fontWeight: customTokens.fontWeightRegular,
    ...shorthands.borderColor("transparent"),
    ":hover": {
      ...shorthands.borderColor("transparent"),
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverBorderStyles,
      },
    },
    ":hover:active": {
      ...shorthands.borderColor("transparent"),
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverBorderStyles,
      },
    },
    // Screen reader text
    [`& .${hiddenContentClassName}`]: {
      ...hiddenContentStyle,
    },
  },
  disabled: {
    pointerEvents: "none",
    backgroundColor: customTokens.colorTransparentBackground,
    ...shorthands.borderColor("transparent"),

    ":hover": {
      backgroundColor: customTokens.colorTransparentBackgroundHover,
      ...shorthands.borderColor("transparent"),
    },

    ":hover:active": {
      backgroundColor: customTokens.colorTransparentBackgroundPressed,
      ...shorthands.borderColor("transparent"),
    },
  },
  small: {
    minWidth: "24px",
    height: "24px",
    ...shorthands.padding("3px"),
  },
  medium: {
    minWidth: "32px",
    ...shorthands.padding("5px"),
  },
  large: {
    minWidth: "40px",
    height: "72px",
    flexDirection: "column",
    justifyContent: "flex-start",
    ...shorthands.padding("1px"),
    // Menu Button/Split Button text container styles
    [`& .${menuTextChevronClass}`]: {
      width: "8px",
      height: "8px",
      lineHeight: "8px",
      paddingLeft: "2px",
      paddingTop: "1px",
      paddingBottom: "4px",
      fill: customTokens.colorNeutralForeground1,
    },
    [`& .${menuTextContainerClass}`]: {
      fontSize: customTokens.fontSizeBase200,
      lineHeight: "normal",
    },
    [`& .${menuTextLine2Class}`]: {
      fontSize: customTokens.fontSizeBase200,
      lineHeight: customTokens.lineHeightBase200,
    },
  },
  groupLauncher: {
    ...shorthands.padding("0px"),
    alignSelf: "center",
    width: "12px",
    minWidth: "12px",
    minHeight: "12px",
    height: "12px",
  },
  groupLauncherWithMinTargetSizes: {
    marginBottom: "8px",
  },
  ribbonModeButton: {
    width: "20px",
    minWidth: "20px",
    minHeight: "20px",
    height: "20px",
  },
  minTargetSizes: {
    ...minimumTargetSizedStyles,
  },
});

const useCommonDisabledTooltipStyles = makeStyles({
  disabled: {
    pointerEvents: "auto",
  },
});

const useCommonRibbonButtonIconStyles = makeStyles({
  base: {
    fontSize: "20px",
    height: "20px",
    width: "20px",
    marginRight: "0px",
    "& i": {
      width: "inherit",
    },
  },
  small: {
    fontSize: "16px",
    height: "16px",
    width: "18px",
    "& i": {
      height: "16px",
      width: "16px",
      marginLeft: "2px",
    },
  },
  large: {
    fontSize: "32px",
    height: "32px",
    width: "32px",
    lineHeight: "32px",
  },
  groupLauncher: {
    fontSize: "8px",
    height: "8px",
    width: "8px",
  },
  ribbonModeButton: {
    fontSize: "12px",
    height: "12px",
    width: "12px",
  },
  ribbonModeButtonMultiline: {
    transform: "rotate(180deg)",
  },
  floatieLargeButton: {
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: `${floatieStylingConstants.floatieLargeIconSize}px`,
    height: "100%",
    width: `${floatieStylingConstants.floatieLargeIconSize}px`,
  },
});

const useCommonRibbonButtonIconStylesNextDeprecated = makeStyles({
  base: {
    ...shorthands.margin(customTokens.spacingHorizontalNone),
    "& i": {
      width: "inherit",
      height: "inherit",
    },
  },
  small: {
    fontSize: customTokens.fontSizeBase400,
    height: "20x",
    width: "20x",
    lineHeight: customTokens.lineHeightBase300,
    ...shorthands.margin(customTokens.spacingHorizontalNone),
    "& i": {
      height: "16px",
      width: "16px",
    },
  },
  medium: {
    fontSize: customTokens.fontSizeBase500,
    height: "20px",
    width: "20px",
    lineHeight: customTokens.lineHeightBase300,
  },
  large: {
    fontSize: customTokens.fontSizeHero800,
    height: "32px",
    width: "32px",
    lineHeight: customTokens.lineHeightBase600,
    ...shorthands.padding(
      customTokens.spacingVerticalNone,
      customTokens.spacingHorizontalXXS,
      customTokens.spacingVerticalXXS,
      customTokens.spacingHorizontalXXS
    ),
    marginTop: customTokens.spacingVerticalNone,
  },
  groupLauncher: {
    fontSize: "8px",
    height: "8px",
    width: "8px",
    lineHeight: "8px",
    ...shorthands.margin(customTokens.spacingHorizontalNone),
  },
  ribbonModeButton: {
    fontSize: customTokens.fontSizeBase200,
    height: "12px",
    width: "12px",
    lineHeight: "12px",
    ...shorthands.margin(customTokens.spacingHorizontalNone),
  },
  ribbonModeButtonMultiline: {
    transform: "rotate(180deg)",
  },
});

const useCommonRibbonButtonIconStylesNext = makeStyles({
  base: {
    ...shorthands.margin(customTokens.spacingHorizontalNone),
    "& i": {
      width: "inherit",
      height: "inherit",
    },
  },
  small: {
    fontSize: customTokens.fontSizeBase400,
    height: "20x",
    width: "20x",
    lineHeight: customTokens.lineHeightBase300,
    ...shorthands.margin(customTokens.spacingHorizontalNone),
    "& i": {
      height: "16px",
      width: "16px",
    },
  },
  medium: {
    fontSize: customTokens.fontSizeBase500,
    height: "20px",
    width: "20px",
    lineHeight: customTokens.lineHeightBase300,
  },
  large: {
    fontSize: customTokens.fontSizeHero800,
    height: "24px",
    width: "24px",
    lineHeight: "0px",
    ...shorthands.padding(
      customTokens.spacingVerticalXS,
      customTokens.spacingHorizontalSNudge,
      customTokens.spacingVerticalSNudge,
      customTokens.spacingHorizontalSNudge
    ),
    marginTop: customTokens.spacingVerticalNone,
  },
  groupLauncher: {
    fontSize: "8px",
    height: "8px",
    width: "8px",
    lineHeight: "8px",
    ...shorthands.margin(customTokens.spacingHorizontalNone),
  },
  ribbonModeButton: {
    fontSize: customTokens.fontSizeBase200,
    height: "12px",
    width: "12px",
    lineHeight: "12px",
    ...shorthands.margin(customTokens.spacingHorizontalNone),
  },
  ribbonModeButtonMultiline: {
    transform: "rotate(180deg)",
  },
});

/**
 * checks whether the state/type of the ribbon button and applies the style based on that
 */
type RibbonButtonStyleType = {
  variant?: RibbonButtonState["variant"];
  size: RibbonButtonState["size"];
};

export const getRibbonButtonTypeStyles = (
  state: RibbonButtonStyleType,
  styles: Record<string, string>
) => {
  if (state.variant === "ribbonModeButton") {
    return mergeClasses(
      "ms-RibbonButton--ribbonMode",
      styles.ribbonModeButton,
      styles.minTargetSizes
    );
  }
  if (state.variant === "groupLauncher") {
    return mergeClasses(
      styles.groupLauncher,
      styles.minTargetSizes,
      styles.groupLauncherWithMinTargetSizes
    );
  }
  return getRibbonButtonSizedStyles(state.size, styles);
};

/**
 * checks the size of the ribbon button and applies the style based on the size
 */
export const getRibbonButtonSizedStyles = (
  size: "small" | "medium" | "large",
  styles: Record<string, string>
) => {
  if (size === ActionControlSize.Small.toLowerCase()) {
    return mergeClasses(styles.small, styles.withMinTargetSize);
  } else if (size === ActionControlSize.Large.toLowerCase()) {
    return styles.large;
  }
  return mergeClasses(styles.medium, styles.withMinTargetSize);
};

/**
 * returns a state where all common ribbon button style are applied on the ribbon button
 */
export const useCommonRibbonButtonStyles = (
  state: RibbonButtonState
): RibbonButtonState => {
  const appContext = React.useContext(AppContext);
  const isNewFluentIconsEnabled = checkExperiment(
    appContext,
    ribbonExperiments.isNewFluentIconsEnabled
  );
  const isDisabledTooltipsEnabled = checkExperiment(
    appContext,
    ribbonExperiments.isDisabledTooltipsEnabled
  );

  const commonStyles = useCommonRibbonButtonRootStyles();
  const commonIconStyles = useCommonRibbonButtonIconStyles();
  const commonFocusStyles = useCommonFocusStyles();
  const commonStylesNext = useCommonRibbonButtonRootStylesNext();
  const commonDisabledTooltipStyles = useCommonDisabledTooltipStyles();
  const commonIconStylesNext = useCommonRibbonButtonIconStylesNext();
  const commonIconStylesNextDeprecated =
    useCommonRibbonButtonIconStylesNextDeprecated();
  const disabledStyles = useCommonDisabledStyles();

  if (isNextStyleEnabled()) {
    state.root.className = mergeClasses(
      "ms-Button",
      "ms-ButtonNext",
      commonStylesNext.base,
      getRibbonButtonTypeStyles(state, commonStylesNext),
      (state.disabled || state.disabledFocusable) && commonStylesNext.disabled,
      (state.disabled || state.disabledFocusable) &&
        isDisabledTooltipsEnabled &&
        commonDisabledTooltipStyles.disabled,
      state.root.className
    );

    if (isNewFluentIconsEnabled) {
      if (state.icon) {
        state.icon.className = mergeClasses(
          "ms-RibbonButton-icon",
          commonIconStylesNext.base,
          (state.disabled || state.disabledFocusable) && disabledStyles.base,
          getRibbonButtonTypeStyles(state, commonIconStylesNext),
          state.variantId === "ribbonModeButtonMultiline" &&
            commonIconStylesNext.ribbonModeButtonMultiline,
          state.icon.className
        );
      }
    } else {
      if (state.icon) {
        state.icon.className = mergeClasses(
          "ms-RibbonButton-icon",
          commonIconStylesNextDeprecated.base,
          (state.disabled || state.disabledFocusable) && disabledStyles.base,
          getRibbonButtonTypeStyles(state, commonIconStylesNextDeprecated),
          state.variantId === "ribbonModeButtonMultiline" &&
            commonIconStylesNextDeprecated.ribbonModeButtonMultiline,
          state.icon.className
        );
      }
    }
  } else {
    state.root.className = mergeClasses(
      "ms-Button",
      commonStyles.base,
      commonStyles.pixelFocusBorder,
      commonFocusStyles.base,
      (state.disabled || state.disabledFocusable) && commonStyles.disabled,
      (state.disabled || state.disabledFocusable) &&
        isDisabledTooltipsEnabled &&
        commonDisabledTooltipStyles.disabled,
      getRibbonButtonTypeStyles(state, commonStyles),
      state.variant === "floatieButton" && commonStyles.floatieButton,
      state.variant === "floatieLargeButton" && commonStyles.floatieLargeButton,
      state.variant === "floatieButton" &&
        state.size === ActionControlSize.Large.toLowerCase() &&
        commonStyles.floatieLargeButton,
      state.root.className
    );

    if (state.icon) {
      state.icon.className = mergeClasses(
        "ms-RibbonButton-icon",
        commonIconStyles.base,
        (state.disabled || state.disabledFocusable) && disabledStyles.base,
        getRibbonButtonTypeStyles(state, commonIconStyles),
        state.variantId === "ribbonModeButtonMultiline" &&
          commonIconStyles.ribbonModeButtonMultiline,
        state.variant === "floatieLargeButton" &&
          commonIconStyles.floatieLargeButton,
        state.variant === "floatieButton" &&
          state.size === ActionControlSize.Large.toLowerCase() &&
          commonIconStyles.floatieLargeButton,
        state.icon.className
      );
    }
  }

  return state;
};
